// ---------------------------------------------------------------------------
// Making Clerk's sign-in card look like the rest of the site.
//
// The card is Clerk's own DOM, rendered by clerk-js outside React, so there are
// exactly two levers: `elements`, which takes class names, and `variables`,
// which takes values. Both are used, for different halves of the problem:
//
//   - `elements` gets this project's Tailwind token utilities. Those compile to
//     var(--c-*), so every surface named here follows [data-theme] with no work
//     and both themes are correct by construction.
//   - `variables` covers what cannot be named: spinners, field errors, focus
//     rings, and the shades Clerk derives itself from the ones it is given.
//
// Clerk parses those colour values with its own runtime parser to build those
// shades, and OKLCH is not something it can be relied on to understand. So the
// tokens are read off the root element and normalised to rgb() by the browser
// before they are handed over. Nothing is hard-coded: the values still come from
// --c-* in index.css, and they are re-read whenever the theme attribute changes.
// A browser that gives us no canvas simply gets the class names, which are the
// half that carries the layout and the two big surfaces anyway.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import type { ClerkProviderProps } from '@clerk/clerk-react';

type ClerkAppearance = NonNullable<ClerkProviderProps['appearance']>;

/** Clerk variable name to the design token it should take its value from. */
const COLOR_TOKENS: Readonly<Record<string, string>> = {
  colorBackground: '--c-surface',
  colorForeground: '--c-ink',
  colorMuted: '--c-surface-2',
  colorMutedForeground: '--c-ink-muted',
  colorInput: '--c-surface-2',
  colorInputForeground: '--c-ink',
  colorBorder: '--c-line',
  colorPrimary: '--c-blue-500',
  colorPrimaryForeground: '--c-ink',
  colorNeutral: '--c-ink',
  colorRing: '--c-blue-400',
  colorDanger: '--c-alert',
  colorSuccess: '--c-good',
  colorWarning: '--c-accent',
  colorModalBackdrop: '--c-scrim',
};

/**
 * Any CSS colour to the rgb() string the browser itself computed for it. The
 * canvas is the shortest route to a colour conversion that is guaranteed to
 * agree with what the page actually paints. A sentinel distinguishes "did not
 * parse" from a value that happens to be the same colour as the sentinel.
 */
function normalizeColor(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  const context = document.createElement('canvas').getContext('2d');
  if (!context) return null;

  const sentinel = '#010203';
  context.fillStyle = sentinel;
  context.fillStyle = trimmed;
  return context.fillStyle === sentinel ? null : String(context.fillStyle);
}

function resolveColors(): Record<string, string> {
  if (typeof document === 'undefined') return {};

  const computed = getComputedStyle(document.documentElement);
  const resolved: Record<string, string> = {};

  for (const [variable, token] of Object.entries(COLOR_TOKENS)) {
    const color = normalizeColor(computed.getPropertyValue(token));
    if (color !== null) resolved[variable] = color;
  }

  return resolved;
}

const ELEMENT_CLASSES: Readonly<Record<string, string>> = {
  cardBox: 'rounded-frame border border-line',
  card: 'bg-surface',
  headerTitle: 'text-ink font-extrabold tracking-[-0.02em]',
  headerSubtitle: 'text-ink-muted',
  socialButtonsBlockButton: 'min-h-12 rounded-[11px] border border-line bg-surface-2 text-ink',
  socialButtonsBlockButtonText: 'text-ink font-semibold',
  dividerLine: 'bg-line',
  dividerText: 'label-mono',
  formFieldLabel: 'label-mono',
  formFieldInput: 'min-h-12 rounded-[11px] border border-line bg-surface-2 text-ink',
  formButtonPrimary:
    'btn-primary-surface min-h-12 rounded-[11px] text-[15px] font-bold normal-case tracking-normal',
  formFieldInputShowPasswordButton: 'text-ink-dim',
  footer: 'bg-surface-2',
  footerActionText: 'text-ink-muted',
  footerActionLink: 'text-blue-200 font-semibold',
  identityPreviewText: 'text-ink',
  identityPreviewEditButton: 'text-blue-200',
  otpCodeFieldInput: 'border-line bg-surface-2 text-ink',
  modalContent: 'rounded-frame',
};

/**
 * Rebuilt on a theme change rather than once at mount: the sign-in card can be
 * open while someone switches the theme, and half a card in the other palette
 * is worse than either palette. `[data-theme]` is what ThemeSync stamps, so
 * watching that attribute catches both the explicit setting and the OS one.
 */
export function useClerkAppearance(): ClerkAppearance {
  const [colors, setColors] = useState<Record<string, string>>(resolveColors);

  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveColors()));
    observer.observe(document.documentElement, { attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return useMemo(
    () => ({
      variables: {
        ...colors,
        fontFamily: 'var(--font-sans)',
        fontFamilyButtons: 'var(--font-sans)',
        borderRadius: '11px',
      },
      elements: ELEMENT_CLASSES,
    }),
    [colors],
  );
}
